import React from 'react';

import {
  TextField,
  Autocomplete,
  InputLabel as BaseInputLabel,
  Box
} from '@mui/material';
import { styled } from '@mui/material/styles';
import _ from 'lodash';
import {
  Controller,
  FieldValues
} from 'react-hook-form';

import { IGenericEnum } from '../../../enumCache'; // TODO: Move this file to layer 3
import {
  IBaseEnumSelectFieldProps,
  IEnumFieldControllerProps,
  IEnumSelectFieldProps,
  IOption,
  IUseGetEnumValuesTuple
} from '../shared/enumSelectFieldTypes';
import * as SelectOptionItem from '../shared/SelectOptionItem';



const InputLabel = styled(BaseInputLabel, {
  shouldForwardProp: prop => prop !== 'isDisabled'
})<{isDisabled?: boolean}>(({ isDisabled }) => ({
  color: '#486C82',
  opacity: isDisabled ? 0.38 : 1,
  fontSize: 12
}));



export const BaseEnumSelectField: React.FC<IBaseEnumSelectFieldProps> = props => {
  const options = React.useMemo(
    (): IOption[] => _.filter(props.options, opt => _.isEmpty(props.hideOptions || []) || !_.includes(props.hideOptions, opt.id)),
    [props.options, props.hideOptions]
  );

  const optionsById = React.useMemo(
    () => _.keyBy(options, option => option.id),
    [options]
  );

  const selectedOptions = React.useMemo(
    () => {
      if (!props.optionId) return null;
      return optionsById[props.optionId];
    },
    [props.optionId, optionsById]
  );

  function handleChange(value: IOption | null) {
    props.onChangeOptionId(value?.id);
  }



  return (
    <Box>
      <InputLabel isDisabled={props.isDisabled}>
        {props.label}
      </InputLabel>

      <Autocomplete
        options={options}
        value={selectedOptions}
        onChange={(e, value) => handleChange(value)}
        getOptionLabel={option => option.label}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        disabled={props.isDisabled}
        renderOption={(props, option, state) => (
          <SelectOptionItem.FC
            {...props}
            variant="checkmark"
            key={option.id}
            text={option.label}
            selected={state.selected}
          />
        )}
        renderInput={params => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={props.placeholder}
            error={!!props.errorMessage}
            InputProps={{
              ...params.InputProps
            }}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (optionsById[e.target.value]) {
                handleChange(optionsById[e.target.value]);
              }
            }}
            helperText={props.errorMessage} />
        )} />
    </Box>
  );
};



export function genEnumSelectFieldComponent<TEnumName extends string, TEnum extends IGenericEnum<TEnumName>>(
  useGetEnumValues: () => IUseGetEnumValuesTuple<TEnumName, TEnum>,
  selectFieldOptions?: {
    label?: React.ReactNode;
  }
) {
  const EnumSelectField: React.FC<IEnumSelectFieldProps<TEnumName>> = props => {
    const tuple = useGetEnumValues();

    const options = React.useMemo(
      () =>
        tuple.loading ?
          [] :
          _.map(tuple.data.enumValues, (enumValue): IOption => ({
            id: enumValue.name,
            label: enumValue.displayName
          })),
      [
        tuple.loading,
        tuple.data.enumValues
      ]
    );



    return (
      <BaseEnumSelectField
        {...props}
        label={selectFieldOptions?.label}
        options={options}
        placeholder={props.placeholder}
        optionId={props.optionId}
        hideOptions={props.hideOptions}
        onChangeOptionId={(optionId?: string) => props.onChangeOptionId(optionId)}
        isDisabled={props.isDisabled || tuple.loading} />
    );
  };

  return EnumSelectField;
}



export function genEnumSelectFieldControllerComponent<TFormState extends FieldValues, TOptionName extends string>(
  Component: React.FC<IEnumSelectFieldProps<TOptionName>>
) {
  const EnumSelectFieldController: React.FC<IEnumFieldControllerProps<TFormState, TOptionName>> = props => {
    return (
      <Controller
        name={props.fieldKey}
        control={props.formControl}
        rules={{ required: props.isRequired }}
        render={({ field, fieldState }) => (
          <Component
            label={props.label}
            hideOptions={props.hideOptions}
            optionId={field.value}
            placeholder={props.placeholder}
            onChangeOptionId={field.onChange}
            errorMessage={fieldState.error?.message}
            isDisabled={props.isDisabled} />
        )} />
    );
  };

  return EnumSelectFieldController;
}



export function genBasicSelectFieldControllerComponent<TFormState extends FieldValues, TOptionName extends string>(
  options: [id: TOptionName, label: string][]
) {
  const EnumSelectFieldController: React.FC<IEnumFieldControllerProps<TFormState, TOptionName>> = props => {
    return (
      <Controller
        name={props.fieldKey}
        control={props.formControl}
        rules={{ required: props.isRequired }}
        render={({ field, fieldState }) => (
          <BaseEnumSelectField
            label={props.label}
            optionId={field.value}
            options={_.map(options, option => ({ id: option[0], label: option[1] }))}
            onChangeOptionId={field.onChange}
            errorMessage={fieldState.error?.message}
            isDisabled={props.isDisabled} />
        )} />
    );
  };

  return EnumSelectFieldController;
}
